import React, { FC, useState, useMemo, useEffect } from "react";
import styled from "@emotion/styled";
import { Text, Flex, Badge } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/modules/api";
import { ComboboxItemSchema } from "@/domains/openapi/types";
import { TERMINAL_FONT_FAMILY } from "./shared/constants";

interface SelectedItem {
  id: string;
  kind: "NOTE" | "COLLECTION";
  label: string;
}

interface SearchableItemSelectorProps {
  selectedItems: SelectedItem[];
  onItemsChange: (items: SelectedItem[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const SearchableItemSelector: FC<SearchableItemSelectorProps> = ({
  selectedItems,
  onItemsChange,
  placeholder = "Search notes or collections to mention...",
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Search for items using the combobox API
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ["combobox-search", searchQuery],
    queryFn: () =>
      api.post("/v2/combobox/search", {
        body: {
          query: searchQuery,
          limit: 10,
        },
      }),
    enabled: searchQuery.length > 2, // Only search when we have at least 3 characters
    staleTime: 5 * 60 * 1000,
  });

  const results = useMemo(() => {
    if (!searchResults?.data?.results) return [];

    // Filter out already selected items and only show NOTE and COLLECTION items
    const filteredResults = searchResults.data.results.filter((item: ComboboxItemSchema) => {
      const isValidKind = item.item_kind === "NOTE" || item.item_kind === "COLLECTION";
      const isNotSelected = !selectedItems.some(selected => selected.id === item.item_id);
      return isValidKind && isNotSelected;
    });

    return filteredResults;
  }, [searchResults, selectedItems]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  const addItem = (item: ComboboxItemSchema) => {
    if (!item.item_kind || (item.item_kind !== "NOTE" && item.item_kind !== "COLLECTION")) return;

    const newItem: SelectedItem = {
      id: item.item_id,
      kind: item.item_kind,
      label: item.primary_label,
    };

    onItemsChange([...selectedItems, newItem]);
    setSearchQuery("");
    setIsSearchVisible(false);
  };

  const removeItem = (itemId: string) => {
    onItemsChange(selectedItems.filter(item => item.id !== itemId));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isSearchVisible || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (results[selectedIndex]) {
          addItem(results[selectedIndex]);
        }
        break;
      case "Escape":
        setSearchQuery("");
        setIsSearchVisible(false);
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsSearchVisible(value.length > 0);
  };

  const getItemIcon = (kind: string) => {
    switch (kind) {
      case "NOTE":
        return "📝";
      case "COLLECTION":
        return "📁";
      default:
        return "📄";
    }
  };

  const getItemColor = (kind: string) => {
    switch (kind) {
      case "NOTE":
        return "blue";
      case "COLLECTION":
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <Container>
      {selectedItems.length > 0 && (
        <SelectedItemsContainer>
          <Flex gap="2" wrap="wrap">
            {selectedItems.map(item => (
              <SelectedItemBadge key={item.id} color={getItemColor(item.kind)} variant="soft">
                <Flex align="center" gap="1">
                  <span>{getItemIcon(item.kind)}</span>
                  <Text size="1">{item.label}</Text>
                  <RemoveButton onClick={() => removeItem(item.id)} disabled={disabled}>
                    ×
                  </RemoveButton>
                </Flex>
              </SelectedItemBadge>
            ))}
          </Flex>
        </SelectedItemsContainer>
      )}

      <SearchContainer>
        <SearchInput
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setIsSearchVisible(searchQuery.length > 0)}
          onBlur={() => {
            // Delay hiding to allow clicking on results
            setTimeout(() => setIsSearchVisible(false), 200);
          }}
        />

        {isSearchVisible && (
          <ResultsContainer>
            {isLoading && (
              <ResultItem>
                <Text size="2" color="gray">
                  Searching...
                </Text>
              </ResultItem>
            )}

            {!isLoading && searchQuery.length <= 2 && (
              <ResultItem>
                <Text size="2" color="gray">
                  Type at least 3 characters to search
                </Text>
              </ResultItem>
            )}

            {!isLoading && searchQuery.length > 2 && results.length === 0 && (
              <ResultItem>
                <Text size="2" color="gray">
                  No items found
                </Text>
              </ResultItem>
            )}

            {!isLoading &&
              results.map((item: ComboboxItemSchema, index: number) => (
                <ResultItem
                  key={item.item_id}
                  isSelected={index === selectedIndex}
                  onClick={() => addItem(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Flex align="center" gap="2">
                    <span>{getItemIcon(item.item_kind ?? "")}</span>
                    <Text size="2">{item.primary_label}</Text>
                    <Badge color={getItemColor(item.item_kind ?? "")} variant="soft" size="1">
                      {(item.item_kind ?? "unknown").toLowerCase()}
                    </Badge>
                  </Flex>
                </ResultItem>
              ))}
          </ResultsContainer>
        )}
      </SearchContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectedItemsContainer = styled.div`
  margin-bottom: 8px;
`;

const SelectedItemBadge = styled(Badge)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  padding: 0;
  margin-left: 4px;
  line-height: 1;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  border: 1px solid var(--gray-6);
  border-radius: 4px;
  background-color: var(--gray-1);
  color: var(--gray-12);

  &:focus {
    outline: none;
    border-color: var(--blue-8);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--gray-9);
  }
`;

const ResultsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: var(--gray-1);
  border: 1px solid var(--gray-6);
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ResultItem = styled.div<{ isSelected?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  background-color: ${({ isSelected }) => (isSelected ? "var(--blue-3)" : "transparent")};

  &:hover {
    background-color: var(--blue-3);
  }

  &:not(:hover) {
    cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  }
`;
